import React, { useState, useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  RefreshControl,
  Alert,
  Linking,
  Platform,
} from 'react-native';
import {
  Text,
  Avatar,
  Button,
  useTheme,
  List,
  Surface,
  ActivityIndicator,
  TouchableRipple,
} from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '../store';
import { refreshUser } from '../store/slices/authSlice';
import { useSettingsNavigation } from '../hooks/useSettingsNavigation';
import {
  fetchUserProfile,
  selectUserProfile,
  selectUserLoading,
  selectUserUpdating,
} from '../store/slices/userSlice';
import {
  Heart,
  Settings2,
  FileText,
  LogOut,
  BellRing,
  Shield,
  HelpCircle,
  ArrowUpRight,
  Edit3,
  Download,
  Trash2,
  MessageSquare,
  ChevronRight,
  User,
  MapPin,
  Globe,
} from 'lucide-react-native';
import Toast from 'react-native-toast-message';

import { useLogout } from '../hooks/useLogout';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { logger } from '../utils/logger';

export function ProfileScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();

  // Auth state
  const { user } = useAppSelector(state => state.auth);

  // User state
  const profile = useAppSelector(selectUserProfile);
  const isUserLoading = useAppSelector(selectUserLoading);

  // Local state
  const [refreshing, setRefreshing] = useState(false);

  // Hooks
  const { logout } = useLogout();
  useSettingsNavigation();

  // Load data on screen focus
  useFocusEffect(
    useCallback(() => {
      loadProfileData();
    }, [])
  );

  const loadProfileData = async () => {
    try {
      // ✅ Get user data first
      const userResult = await dispatch(refreshUser())
        .unwrap()
        .catch(err => {
          logger.warn('Failed to refresh user:', err);
          return null;
        });
      // ✅ Pass userId to fetchUserProfile
      const userId = userResult?.id || user?.id;

      if (userId) {
        await dispatch(fetchUserProfile(userId)).catch(err => {
          logger.warn('Failed to fetch profile:', err);
        });
      } else {
        logger.error('❌ No userId available for profile fetch');
      }
    } catch (error) {
      logger.error('Error loading profile data:', error);
      if (error instanceof Error && error.message.includes('network')) {
        Toast.show({
          type: 'error',
          text1: 'Connection Error',
          text2: 'Please check your internet connection',
        });
      }
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadProfileData();
    setRefreshing(false);
  };

  const handleExportData = () => {
    Alert.alert(
      'Export Data',
      'This will generate a download link for all your data. You will receive an email when ready.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Export',
          onPress: async () => {
            try {
              // TODO: Implement actual export API call
              Toast.show({
                type: 'info',
                text1: 'Export requested',
                text2: 'You will receive an email when ready',
              });
            } catch (error) {
              logger.error('Export data error:', error);
              Toast.show({
                type: 'error',
                text1: 'Export failed',
                text2: 'Please try again later',
              });
            }
          },
        },
      ]
    );
  };

  const handleDeleteAccount = () => {
    Alert.alert(
      'Delete Account',
      'This action cannot be undone. All your data will be permanently deleted.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            navigation.navigate('deleteAccount' as never);
          },
        },
      ]
    );
  };

  const openExternalLink = (url: string) => {
    Linking.openURL(url).catch(() => {
      Toast.show({
        type: 'error',
        text1: 'Failed to open link',
      });
    });
  };

  // Helper functions
  const getDisplayName = useCallback(() => {
    if (user?.display_name) return user.display_name;
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`.trim();
    }
    if (user?.first_name) return user.first_name;
    if (user?.username) return user.username;
    return 'Coffee Enthusiast';
  }, [user]);

  const getInitials = useCallback(() => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user?.first_name) return user.first_name[0].toUpperCase();
    if (user?.username) return user.username[0].toUpperCase();
    return 'CE';
  }, [user]);

  const getUsername = useCallback(() => {
    return user?.username;
  }, [user]);

  const ICON_SIZE = 20;
  const ICON_STROKE_WIDTH = 1.5;
  const ARROW_SIZE = 16;

  const menuSections = [
    {
      title: 'My Coffee',
      items: [
        {
          title: 'My Recipes',
          icon: FileText,
          onPress: () => navigation.navigate('myRecipes' as never),
        },
        {
          title: 'Favorites',
          icon: Heart,
          onPress: () => navigation.navigate('favorites' as never),
        },
        {
          title: 'My Comments',
          icon: MessageSquare,
          onPress: () => navigation.navigate('myComments' as never),
        },
      ],
    },
    {
      title: 'Account',
      items: [
        {
          title: 'Edit Profile',
          icon: Edit3,
          onPress: () => navigation.navigate('editProfile' as never),
        },
        {
          title: 'Notifications',
          icon: BellRing,
          onPress: () => navigation.navigate('notificationSettings' as never),
        },
        {
          title: 'Settings',
          icon: Settings2,
          onPress: () => navigation.navigate('settings' as never),
        },
        {
          title: 'Privacy & Security',
          icon: Shield,
          onPress: () => navigation.navigate('privacySecurity' as never),
        },
      ],
    },
    {
      title: 'Support & Legal',
      items: [
        {
          title: 'Help & Support',
          icon: HelpCircle,
          onPress: () => navigation.navigate('helpSupport' as never),
        },
        {
          title: 'Terms of Service',
          icon: ArrowUpRight,
          onPress: () => openExternalLink('https://coffeeplexity.com/terms'),
        },
        {
          title: 'Privacy Policy',
          icon: ArrowUpRight,
          onPress: () => openExternalLink('https://coffeeplexity.com/privacy'),
        },
        {
          title: 'Export My Data',
          icon: Download,
          onPress: handleExportData,
        },
      ],
    },
  ];

  const renderSectionInfo = (section: (typeof menuSections)[0]) => (
    <View key={section.title} style={styles.sectionContainer}>
      <Text
        variant='labelMedium'
        style={[styles.sectionTitle, { color: theme.colors.onSurfaceVariant }]}
      >
        {section.title}
      </Text>
      <Surface
        style={[
          styles.sectionSurface,
          { backgroundColor: theme.colors.surface },
        ]}
        elevation={0}
      >
        {section.items.map((item, index) => (
          <React.Fragment key={item.title}>
            <TouchableRipple
              onPress={item.onPress}
              style={[
                styles.menuItem,
                index === 0 && styles.firstMenuItem,
                index === section.items.length - 1 && styles.lastMenuItem,
              ]}
            >
              <View style={styles.menuItemContent}>
                <View style={styles.menuItemLeft}>
                  <View
                    style={[
                      styles.iconContainer,
                      { backgroundColor: theme.colors.surfaceVariant },
                    ]}
                  >
                    <item.icon
                      size={ICON_SIZE}
                      color={theme.colors.onSurface}
                      strokeWidth={ICON_STROKE_WIDTH}
                    />
                  </View>
                  <Text
                    variant='bodyLarge'
                    style={[
                      styles.menuItemText,
                      { color: theme.colors.onSurface },
                    ]}
                  >
                    {item.title}
                  </Text>
                </View>
                <ChevronRight
                  size={ARROW_SIZE}
                  color={theme.colors.onSurfaceVariant}
                  strokeWidth={ICON_STROKE_WIDTH}
                />
              </View>
            </TouchableRipple>
            {index < section.items.length - 1 && (
              <View
                style={[
                  styles.divider,
                  { backgroundColor: theme.colors.outlineVariant },
                ]}
              />
            )}
          </React.Fragment>
        ))}
      </Surface>
    </View>
  );

  if (!user && isUserLoading) {
    return (
      <ErrorBoundary>
        <SafeAreaView
          style={[
            styles.container,
            { backgroundColor: theme.colors.background },
          ]}
        >
          <View style={styles.loadingContainer}>
            <ActivityIndicator size='large' color={theme.colors.primary} />
            <Text
              variant='bodyMedium'
              style={[
                styles.loadingText,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Loading profile...
            </Text>
          </View>
        </SafeAreaView>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
        edges={['top']}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[theme.colors.primary]}
              tintColor={theme.colors.primary}
            />
          }
        >
          {/* Profile Header */}
          <View style={styles.header}>
            <View style={styles.avatarContainer}>
              {user?.avatar_url ? (
                <Avatar.Image
                  size={88}
                  source={{ uri: user.avatar_url }}
                  accessibilityLabel={`Profile picture of ${getDisplayName()}`}
                />
              ) : (
                <Avatar.Text
                  size={88}
                  label={getInitials()}
                  style={{ backgroundColor: theme.colors.primaryContainer }}
                  color={theme.colors.primary}
                  labelStyle={{ fontWeight: 'bold' }}
                />
              )}
            </View>

            <View style={styles.profileInfo}>
              <Text
                variant='headlineMedium'
                style={[styles.name, { color: theme.colors.onBackground }]}
              >
                {getDisplayName()}
              </Text>
              {getUsername() && (
                <Text
                  variant='bodyMedium'
                  style={[styles.username, { color: theme.colors.primary }]}
                >
                  @{getUsername()}
                </Text>
              )}

              {(profile?.location || profile?.website) && (
                <View style={styles.metaContainer}>
                  {profile?.location && (
                    <View style={styles.metaItem}>
                      <MapPin
                        size={12}
                        color={theme.colors.onSurfaceVariant}
                        strokeWidth={1.5}
                      />
                      <Text
                        variant='bodySmall'
                        style={[
                          styles.metaText,
                          { color: theme.colors.onSurfaceVariant },
                        ]}
                      >
                        {profile.location}
                      </Text>
                    </View>
                  )}
                  {profile?.website && (
                    <View style={styles.metaItem}>
                      <Globe
                        size={12}
                        color={theme.colors.onSurfaceVariant}
                        strokeWidth={1.5}
                      />
                      <Text
                        variant='bodySmall'
                        style={[
                          styles.metaText,
                          { color: theme.colors.primary },
                        ]}
                        onPress={() => openExternalLink(profile.website!)}
                      >
                        {profile.website.replace(/^https?:\/\//, '')}
                      </Text>
                    </View>
                  )}
                </View>
              )}

              {profile?.bio && (
                <Text
                  variant='bodyMedium'
                  style={[styles.bio, { color: theme.colors.onSurfaceVariant }]}
                  numberOfLines={2}
                >
                  {profile.bio}
                </Text>
              )}
            </View>
          </View>

          {/* Menu Sections */}
          <View style={styles.sectionsContainer}>
            {menuSections.map(renderSectionInfo)}

            {/* Danger Zone */}
            <View style={[styles.sectionContainer, styles.lastSection]}>
              <Surface
                style={[
                  styles.sectionSurface,
                  { backgroundColor: theme.colors.surface },
                ]}
                elevation={0}
              >
                <TouchableRipple
                  onPress={() => logout()}
                  style={[styles.menuItem, styles.firstMenuItem]}
                >
                  <View style={styles.menuItemContent}>
                    <View style={styles.menuItemLeft}>
                      <View
                        style={[
                          styles.iconContainer,
                          { backgroundColor: theme.colors.errorContainer },
                        ]}
                      >
                        <LogOut
                          size={ICON_SIZE}
                          color={theme.colors.error}
                          strokeWidth={ICON_STROKE_WIDTH}
                        />
                      </View>
                      <Text
                        variant='bodyLarge'
                        style={[
                          styles.menuItemText,
                          { color: theme.colors.error },
                        ]}
                      >
                        Log Out
                      </Text>
                    </View>
                  </View>
                </TouchableRipple>

                <View
                  style={[
                    styles.divider,
                    { backgroundColor: theme.colors.outlineVariant },
                  ]}
                />

                <TouchableRipple
                  onPress={handleDeleteAccount}
                  style={[styles.menuItem, styles.lastMenuItem]}
                >
                  <View style={styles.menuItemContent}>
                    <View style={styles.menuItemLeft}>
                      <View
                        style={[
                          styles.iconContainer,
                          { backgroundColor: theme.colors.surfaceVariant },
                        ]}
                      >
                        <Trash2
                          size={ICON_SIZE}
                          color={theme.colors.onSurfaceVariant}
                          strokeWidth={ICON_STROKE_WIDTH}
                        />
                      </View>
                      <Text
                        variant='bodyLarge'
                        style={[
                          styles.menuItemText,
                          { color: theme.colors.onSurfaceVariant },
                        ]}
                      >
                        Delete Account
                      </Text>
                    </View>
                  </View>
                </TouchableRipple>
              </Surface>
            </View>

            {/* Version Info */}
            <Text
              variant='labelSmall'
              style={[styles.version, { color: theme.colors.onSurfaceVariant }]}
            >
              Version 1.0.0 (Build 142)
            </Text>
          </View>
        </ScrollView>
      </SafeAreaView>
    </ErrorBoundary>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  header: {
    alignItems: 'center',
    paddingTop: 24,
    paddingHorizontal: 24,
    marginBottom: 32,
  },
  avatarContainer: {
    marginBottom: 16,
    position: 'relative',
  },
  editBadge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
  },
  profileInfo: {
    alignItems: 'center',
    width: '100%',
  },
  name: {
    fontWeight: '700',
    marginBottom: 4,
    letterSpacing: -0.5,
  },
  username: {
    fontWeight: '500',
    marginBottom: 12,
  },
  metaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 16,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaText: {
    opacity: 0.8,
  },
  bio: {
    textAlign: 'center',
    maxWidth: '80%',
    lineHeight: 20,
    opacity: 0.8,
  },
  sectionsContainer: {
    paddingHorizontal: 16,
    gap: 24,
  },
  sectionContainer: {
    gap: 8,
  },
  sectionTitle: {
    marginLeft: 16,
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontSize: 11,
    fontWeight: '600',
    opacity: 0.7,
  },
  sectionSurface: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  menuItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  firstMenuItem: {
    paddingTop: 16,
  },
  lastMenuItem: {
    paddingBottom: 16,
  },
  menuItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  menuItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  iconContainer: {
    width: 36,
    height: 36,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuItemText: {
    fontWeight: '500',
    fontSize: 15,
  },
  divider: {
    height: 1,
    marginLeft: 68,
    opacity: 0.5,
  },
  lastSection: {
    marginTop: 8,
  },
  version: {
    textAlign: 'center',
    opacity: 0.4,
    marginTop: 8,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
  },
});
